import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Clock3 } from 'lucide-react'
import { aiApi } from '@/api/ai'
import { cn } from '@/components/ui/cn'
import type { PracticeExerciseSetSummary } from '@/types/practiceExerciseSet'

interface PracticeHistoryPanelProps {
  onResume: (set: PracticeExerciseSetSummary) => void
  className?: string
}

function formatWhen(iso: string) {
  const date = new Date(iso)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function PracticeHistoryPanel({ onResume, className }: PracticeHistoryPanelProps) {
  const { data: sets = [], isLoading } = useQuery({
    queryKey: ['practiceExerciseSets'],
    queryFn: () => aiApi.listExerciseSets(8).then((r) => r.data),
  })

  if (isLoading || sets.length === 0) {
    return null
  }

  return (
    <div className={cn('rounded-xl border bg-card p-4', className)} data-testid="practice-history-panel">
      <h3 className="text-sm font-semibold text-foreground">Recent lessons</h3>
      <ul className="mt-3 space-y-2">
        {sets.map((set) => (
          <li key={set.id}>
            <button
              type="button"
              onClick={() => onResume(set)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50"
              data-testid={`practice-history-item-${set.id}`}
            >
              <span className="min-w-0 truncate font-medium">{set.title}</span>
              <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                {set.status === 'COMPLETED' ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    {set.scorePercent != null ? `${Math.round(set.scorePercent)}%` : 'Done'}
                  </>
                ) : (
                  <>
                    <Clock3 className="h-3.5 w-3.5" />
                    In progress
                  </>
                )}
                <span aria-hidden>·</span>
                {formatWhen(set.updatedAt)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
